import { useState, type ReactNode } from 'react';
import type { TranscriptEntry, TranscriptRun } from '@deskworks/shared-types';
import {
  AlertTriangle,
  Brain,
  CheckSquare,
  FileCode2,
  Globe,
  Info,
  MessageSquare,
  ScrollText,
  SquareTerminal,
  User,
  Wrench,
} from 'lucide-react';
import { Markdown } from '@/components/markdown';
import { SessionLink } from '@/components/session-link';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatAbsolute, formatDuration, formatTime, prettyJson } from '@/lib/format';
import { intlLocale, t } from '@/lib/i18n';
import { EXECUTION_KIND, EXECUTION_STATUS, RUNTIME_TYPE } from '@/lib/labels';
import { useAutoScroll } from '@/lib/use-auto-scroll';
import { cn } from '@/lib/utils';

/**
 * The agent's whole session as one continuous stream, top to bottom, as its
 * runtime reported it: instructions, messages, reasoning, every command and
 * tool call with output. Each run is its own worker with a fresh runtime
 * session; a divider marks where the next one starts.
 */
export function AgentConversation({
  runs,
  isPending,
  error,
  onRetry,
  emptyDescription = t('As soon as an agent works on this item, its full conversation shows up here.'),
  showRunHeader = true,
}: {
  runs: TranscriptRun[] | undefined;
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  emptyDescription?: string;
  /** Off when the page around it already says which run this is. */
  showRunHeader?: boolean;
}) {
  const list = runs ?? [];
  if (isPending) {
    return (
      <div className="px-5 py-3">
        <LoadingRows rows={4} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-5 py-3">
        <ErrorState error={error} onRetry={onRetry} />
      </div>
    );
  }
  if (list.length === 0) {
    return (
      <div className="px-5 py-3">
        <EmptyState compact title={t('No runs yet')} description={emptyDescription} />
      </div>
    );
  }
  return <Stream runs={list} showRunHeader={showRunHeader} />;
}

function Stream({ runs: list, showRunHeader }: { runs: TranscriptRun[]; showRunHeader: boolean }) {
  const [openAll, setOpenAll] = useState(false);
  const steps = list.reduce((n, r) => n + r.entries.length, 0);
  const ref = useAutoScroll<HTMLDivElement>(steps + list.length);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-5 py-2.5 text-xs text-muted-foreground">
        <span>
          {list.length === 1 ? t('1 run') : t('{n} runs', { n: list.length })} · {steps === 1 ? t('1 step') : t('{n} steps', { n: steps.toLocaleString(intlLocale) })}
        </span>
        {steps > 0 && (
          <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => setOpenAll((v) => !v)}>
            {openAll ? t('Collapse details') : t('Expand all details')}
          </Button>
        )}
      </div>
      <div ref={ref} className="max-h-[640px] min-h-32 overflow-y-auto">
        {list.map((run, i) => (
          <section key={run.executionId}>
            {showRunHeader && <RunDivider run={run} index={i} />}
            {run.entries.length === 0 ? (
              <p className="px-5 py-3 text-xs text-muted-foreground">
                {EXECUTION_STATUS[run.status]?.live
                  ? t('The agent is starting — its steps appear here as they happen.')
                  : t('No steps recorded — this run happened before sessions were recorded, or its runtime reports none.')}
              </p>
            ) : (
              <ol className="flex flex-col divide-y">
                {run.entries.map((e) => (
                  // Remount on toggle so "expand all" also re-opens details the user closed.
                  <Entry key={`${e.id}-${openAll}`} entry={e} openAll={openAll} />
                ))}
              </ol>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

/** Where a new worker — and with it a fresh runtime session — took over. */
function RunDivider({ run, index }: { run: TranscriptRun; index: number }) {
  const status = EXECUTION_STATUS[run.status];
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b bg-muted/40 px-5 py-2 text-xs text-muted-foreground', index > 0 && 'border-t')}>
      <span className="font-medium text-foreground">
        {t('Run {n}', { n: index + 1 })} · {EXECUTION_KIND[run.kind] ?? run.kind}
      </span>
      <Badge tone={status?.tone ?? 'neutral'}>{status?.label ?? run.status}</Badge>
      {run.agentName && <span className="text-foreground">{run.agentName}</span>}
      {run.runtimeType && (
        <span>
          {RUNTIME_TYPE[run.runtimeType] ?? run.runtimeType}
          {run.model && <span className="font-mono"> · {run.model}</span>}
        </span>
      )}
      {run.startedAt && (
        <span title={formatAbsolute(run.startedAt, true)}>
          {t('started {time}', { time: formatTime(run.startedAt) })} · {formatDuration(run.startedAt, run.finishedAt)}
        </span>
      )}
      <SessionLink executionId={run.executionId} label={t('Open in Sessions')} className="ml-auto" />
    </div>
  );
}

const KIND: Record<string, { label: string; icon: ReactNode }> = {
  prompt: { label: t('Instructions'), icon: <ScrollText /> },
  user: { label: t('Message from a person'), icon: <User /> },
  message: { label: t('Agent'), icon: <MessageSquare /> },
  reasoning: { label: t('Reasoning'), icon: <Brain /> },
  command: { label: t('Command'), icon: <SquareTerminal /> },
  tool_call: { label: t('Tool'), icon: <Wrench /> },
  file_change: { label: t('Changed files'), icon: <FileCode2 /> },
  web_search: { label: t('Web search'), icon: <Globe /> },
  todo: { label: t('To-do list'), icon: <CheckSquare /> },
  error: { label: t('Error'), icon: <AlertTriangle /> },
  notice: { label: t('Note'), icon: <Info /> },
};

/** One step of a session (also used by the live "working" panel, components/agent-steps.tsx). */
export function Entry({ entry: e, openAll }: { entry: TranscriptEntry; openAll: boolean }) {
  const meta = KIND[e.kind] ?? { label: e.kind, icon: <Info /> };
  const isError = e.kind === 'error' || e.data.isError === true || (typeof e.data.exitCode === 'number' && e.data.exitCode !== 0);
  return (
    <li className={cn('flex gap-3 px-5 py-3', isError && 'bg-red-50/60 dark:bg-red-950/20')}>
      <div
        className={cn(
          'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border bg-card [&_svg]:size-3.5',
          isError ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground',
        )}
      >
        {meta.icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{e.kind === 'prompt' && e.title ? e.title : meta.label}</span>
          <time dateTime={e.createdAt} title={formatAbsolute(e.createdAt, true)} className="tabular-nums">
            {formatTime(e.createdAt)}
          </time>
        </div>
        <EntryBody entry={e} openAll={openAll} />
      </div>
    </li>
  );
}

function EntryBody({ entry: e, openAll }: { entry: TranscriptEntry; openAll: boolean }) {
  const content = e.content ?? '';
  switch (e.kind) {
    case 'prompt':
      return <Details summary={t('Show ({n} characters)', { n: content.length.toLocaleString(intlLocale) })} text={content} open={openAll} />;
    case 'message':
      return <Markdown text={content} />;
    case 'user':
      return <div className="text-sm whitespace-pre-wrap">{content}</div>;
    case 'reasoning':
      return content.length > 600 && !openAll ? (
        <Details summary={firstLine(content)} text={content} prose />
      ) : (
        <div className="text-sm whitespace-pre-wrap text-muted-foreground italic">{content}</div>
      );
    case 'command': {
      const exit = e.data.exitCode;
      return (
        <div className="flex flex-col gap-1.5">
          <code className="block rounded-md bg-muted px-2.5 py-1.5 font-mono text-xs break-all whitespace-pre-wrap">$ {e.title}</code>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            {typeof exit === 'number' && <span className={cn(exit !== 0 && 'text-red-600 dark:text-red-400')}>{t('exit code {code}', { code: exit })}</span>}
            {typeof e.data.status === 'string' && e.data.status !== 'completed' && <span>{e.data.status}</span>}
          </div>
          {content.trim() ? <Details summary={`${t('Output')} · ${lineCount(content)}`} text={content} open={openAll || lines(content) <= 12} /> : null}
        </div>
      );
    }
    case 'tool_call': {
      const args = parseArgs(e.data.arguments);
      const server = typeof e.data.server === 'string' && e.data.server ? e.data.server : null;
      return (
        <div className="flex flex-col gap-1.5">
          <div className="font-mono text-xs">
            {server && <span className="text-muted-foreground">{server} · </span>}
            <span className="font-semibold">{e.title}</span>
            {typeof e.data.status === 'string' && e.data.status !== 'completed' && <span className="text-muted-foreground"> ({e.data.status})</span>}
          </div>
          {args !== undefined && <Details summary={t('Arguments')} text={typeof args === 'string' ? args : prettyJson(args)} open={openAll} />}
          {content.trim() ? (
            <Details summary={`${e.data.isError ? t('Error') : t('Result')} · ${lineCount(content)}`} text={prettyMaybe(content)} open={openAll || !!e.data.isError} />
          ) : null}
        </div>
      );
    }
    case 'error':
      return <div className="text-sm whitespace-pre-wrap text-red-700 dark:text-red-300">{[e.title, content].filter(Boolean).join(': ')}</div>;
    case 'file_change':
    case 'web_search':
    case 'todo':
      return <pre className="font-mono text-xs whitespace-pre-wrap">{content}</pre>;
    default:
      return (
        <div className="text-sm whitespace-pre-wrap text-muted-foreground">
          {e.title && `${e.title}: `}
          {content}
        </div>
      );
  }
}

function Details({ summary, text, open, prose }: { summary: string; text: string; open?: boolean; prose?: boolean }) {
  return (
    <details open={open} className="group">
      <summary className="cursor-pointer text-xs text-muted-foreground select-none hover:text-foreground">{summary}</summary>
      {prose ? (
        <div className="mt-1.5 text-sm whitespace-pre-wrap text-muted-foreground italic">{text}</div>
      ) : (
        <pre className="mt-1.5 max-h-[480px] overflow-auto rounded-md border bg-muted/50 px-2.5 py-2 font-mono text-xs whitespace-pre-wrap break-words">
          {text}
        </pre>
      )}
    </details>
  );
}

const lines = (text: string) => text.split('\n').length;
const lineCount = (text: string) => (lines(text) === 1 ? t('1 line') : t('{n} lines', { n: lines(text).toLocaleString(intlLocale) }));

function firstLine(text: string): string {
  const line = text.trim().split('\n')[0];
  return line.length > 140 ? `${line.slice(0, 140)}…` : line;
}

function parseArgs(value: unknown): unknown {
  if (value == null) return undefined;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

/** Tool results are often JSON on one line; indent them for reading. */
function prettyMaybe(text: string): string {
  const s = text.trim();
  if (!(s.startsWith('{') || s.startsWith('['))) return text;
  try {
    return prettyJson(JSON.parse(s));
  } catch {
    return text;
  }
}
